import React, { useState, useRef, useEffect, useContext } from 'react';
import XProvider from '@ant-design/x/es/x-provider';
import Bubble from '@ant-design/x/es/bubble';
import Sender from '@ant-design/x/es/sender';
import Attachments from '@ant-design/x/es/attachments';
import Prompts from '@ant-design/x/es/prompts';
import { CloudUploadOutlined, CopyOutlined, PaperClipOutlined, SyncOutlined, UserOutlined } from "@ant-design/icons";
import { Badge, Button, Flex, Input, Space, message as notification } from 'antd';
import CreateTaskHeader from '../../ScrumBoard/Task/TaskCreateOrUpdate/CreateTaskHeader/CreateTaskHeader';
import AIAgentFooter from './AIAgentFooter';
import { copyText } from '../../../context/helper';
import GenerateMessage from './GenerateMessage';
import AIChatHistory from './AIChatHistory';
import { AppContext } from '../../../context/ContextProvider';

const roles = {
    user: {
        avatar: { icon: <UserOutlined />, style: { background: '#fde3cf' } },
        typing: { step: 5, interval: 20 },
        placement: "end",
        styles: {
            content: {
                background: "#6486ee",
                color: "white",
                borderRadius: "20px"
            }
        },
    },
    assistant: {
        placement: 'start',
        // avatar: { icon: <UserOutlined />, style: { background: '#87d068' } },
        styles: {
            content: {
                background: "white",
                border: "1px solid #e2e8f0",
                color: "#1e293b",
                borderRadius: "20px"
            }
        }
    },
};

const outerContainerStyle = {
    height: "calc(100% - 90px)",
    marginTop: "90px"
};

const senderStyle = {
    borderRadius: "0",
    borderColor: "#9e9e9e transparent transparent",
};

const promptItems = [
    {
        key: "1",
        description: "Generate a kubernetes deployment YAML.",
    },
    {
        key: "2",
        description: "Create a Dockerfile."
    },
    {
        key: "3",
        description: "Tell me about DevOps."
    }
];

const promptStyle = {
    title: {
        color: "#5e6366",
        fontSize: "36px",
        fontWeight: "500",
        fontFamily: "Poppins",
        textAlign: "center"
    },
    item: {
        border: "1px solid #6486ee",
        borderRadius: "10px",
    },
    itemContent: {
        color: "#6486ee",
        fontFamily: "Nunito Sans",
        fontWeight: "500",
        fontSize: "14px"
    }
};

export default function AIAgent({ data }) {
    // Not using data snippet for user query now
    const [messages, setMessages] = useState(data?.message ? [] : [{
        content: "Hello! I'm your personal AI Assistant.",
        role: "assistant"
    }]);
    const [message, setMessage] = useState(data?.message ? data.message : "");
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const senderRef = useRef(null);
    const controllerRef = useRef(null);
    const timerRef = useRef(null);
    const [promptScroll, setPromptScroll] = useState(false);
    const hasSeenInitialDebugRequest = useRef(false); // Track if we've sent the initial debug request

    const [openLeftPanel, setOpenLeftPanel] = useState(false);

    const { setOpenRightDrawer } = useContext(AppContext);

    console.log("data: ", data?.title)
    const handleSend = async (userText) => {
        // 1. Add user message and insert assistant placeholder
        let placeholderIndex;
        
        // For debug mode, only skip showing the first auto-triggered message
        // For subsequent user-typed messages, always show them
        const shouldShowUserMessage = !data?.snippet || hasSeenInitialDebugRequest.current;
        
        setMessages(prev => {
            // prev is always the latest array
            const newMsgs = [
                ...prev,
                ...(shouldShowUserMessage ? [{ content: userText, role: 'user', attachments: [...items] }] : []),
                { content: "", role: 'assistant', loading: true, fetching: true } // loading shows the loading state till a responseis received and fetching continues to be true till the entire response is received from the stream
            ];
            // the assistant placeholder is the last element
            placeholderIndex = newMsgs.length - 1;
            return newMsgs;
        });

        try {
            controllerRef.current?.abort();
            controllerRef.current = new AbortController();
            setMessage("");
            setItems([]);
            console.log("data?.title: ", data?.title);
            
            // Determine if this is a debug request and use appropriate endpoint
            const isDebugRequest = data?.isDebug || data?.title === "Debug Panel";
            const apiUrl = isDebugRequest 
                ? `${process.env.REACT_APP_KUBEGATEWAY}/api/chat/bedrock/stream`
                : `${process.env.REACT_APP_API_URI}/api/integration/chatbot/`;
            
            // Prepare request body based on endpoint
            let requestBody;
            if (isDebugRequest) {
                // New endpoint format - swapped for Nova models
                // message: error log + prompt (only for first message)
                // system_message: system context
                
                // Check if this is the first debug request
                const isFirstMessage = !hasSeenInitialDebugRequest.current;
                if (isFirstMessage) {
                    hasSeenInitialDebugRequest.current = true;
                }
                
                const errorLog = data?.snippet || data?.selectedText || "";
                const prompt = userText.includes("Debug the following lines") 
                    ? "Please analyze this error log and answer:\n1. What is the issue with these logs?\n2. What are the possible causes for this issue?\n3. What is the suggested fix for this issue?"
                    : userText;
                
                // Only include error log in the first message
                requestBody = {
                    message: isFirstMessage ? `${errorLog}\n\n${prompt}` : userText,
                    system_message: "You are a DevOps expert specializing in debugging CI/CD pipeline errors, Docker build failures, and Argo Workflows. Analyze error logs and provide clear, actionable solutions."
                };
            } else {
                // Old endpoint format
                requestBody = {
                    message: userText,
                    attachments: [...items],
                    mode: "chat",
                    slug: data?.title == "Debug Panel" ? "cicd-debugger" : data?.title == "Debug with AI" ? "kubescape" : "atmosly"
                };
            }

            const res = await fetch(
                apiUrl,
                {
                    method: 'POST',
                    headers: {
                        "Authorization": `Bearer ${localStorage.getItem("isAuth")}`,
                        "Content-Type": "application/json"
                    },
                    signal: controllerRef.current?.signal,
                    body: JSON.stringify(requestBody),
                }
            );

            if (!res.body) throw new Error('Streaming not supported.');


            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let buffer = "";
            let accumulated = "";
            let aborted = false;                // ← a flag to break out

            while (!aborted) {
                const { done, value } = await reader.read();
                console.log("Done and val: ", done, value);
                if (done) {
                    setMessages((prev) => {
                        const copy = [...prev];
                        copy[placeholderIndex] = {
                            ...copy[placeholderIndex],
                            fetching: false,
                        };
                        return copy;
                    });
                    break;
                }

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split("\n");
                buffer = lines.pop();


                for (const line of lines) {
                    const trimmed = line.trim();
                    if (!trimmed.startsWith("data:")) continue;

                    let payload;
                    try {
                        payload = JSON.parse(trimmed.replace(/^data:\s*/, ""));
                    } catch {
                        console.error("Invalid JSON chunk:", trimmed);
                        continue;
                    }

                    // ———— Handle an error frame ————
                    if (payload.error) {
                        // overwrite assistant placeholder with the error message
                        setMessages((prev) => {
                            const copy = [...prev];
                            copy[placeholderIndex] = {
                                content: (copy[placeholderIndex].content + `\n⚠️ ${payload.error}`),
                                role: "assistant",
                                loading: false,
                                fetching: false
                            };
                            return copy;
                        });
                        aborted = true;
                        reader.cancel();           // stop the fetch stream
                        break;                     // break out of the inner for‐loop
                    }

                    // ———— Normal "textResponse" frame ————
                    // Handle both old format (textResponse) and new format (message or content)
                    const textChunk = payload.textResponse || payload.message || payload.content || "";
                    if (textChunk) {
                        accumulated += textChunk;
                        setMessages((prev) => {
                            const copy = [...prev];
                            copy[placeholderIndex] = {
                                content: accumulated,
                                role: "assistant",
                                loading: false,         // first real chunk → hide spinner
                                fetching: true,
                            };
                            return copy;
                        });
                    }

                    // you can also watch payload.close if you want to force‐end:
                    // if (payload.close) { aborted = true; break; }
                }
            }

            // once we exit the loop, ensure spinner is off (in case no frames arrived)
            setMessages((prev) => {
                const copy = [...prev];
                copy[placeholderIndex] = {
                    ...copy[placeholderIndex],
                    loading: false,
                    fetching: false
                };
                return copy;
            });
        }
        catch (err) {
                setMessages((prev) => {
                    const copy = [...prev];
                    console.log("getting placeholder index: ", placeholderIndex, copy, err.name);
                    copy[placeholderIndex] = {
                        content: (copy[placeholderIndex].content + ((err.name === "AbortError") ? `\n\n⚠️ Error: Request stopped by the user.` : `\n\n⚠️ Error: ${err.message}`)),
                        role: 'assistant',
                        loading: false,
                        fetching: false
                    };
                    return copy;
                });
        }
        finally {
            setMessage("");
            setItems([]);
        }
    };

    const handleChange = (value) => {
        if (value.length <= 5000) {
            setMessage(value);
        }
    }

    console.log("Data message: ", data?.message);
    useEffect(() => {
        if (data?.message && data?.isDebug) {
            // For debug requests, send the prompt directly (error log will be in data.snippet)
            handleSend("Please analyze this error log and answer:\n1. What is the issue with these logs?\n2. What are the possible causes for this issue?\n3. What is the suggested fix for this issue?");
        } else if (data?.message) {
            handleSend(`Debug the following lines:\n\n ${data.message}`);
        }
    }, [data?.message])

    const senderHeader = (
        <Sender.Header
            title="Attachments"
            open={open}
            onOpenChange={setOpen}
            styles={{
                content: {
                    padding: 0,
                },
            }}
        >
            <Attachments
                // Mock not real upload file
                beforeUpload={() => false}
                items={items}
                onChange={({ fileList }) => setItems(fileList)}
                placeholder={type =>
                    type === 'drop'
                        ? {
                            title: 'Drop file here',
                        }
                        : {
                            icon: <CloudUploadOutlined />,
                            title: 'Upload files',
                            description: 'Click or drag files to this area to upload',
                        }
                }
                getDropContainer={() => {
                    var _a;
                    return (_a = senderRef.current) === null || _a === void 0 ? void 0 : _a.nativeElement;
                }}
            />
        </Sender.Header>
    );

    const senderLoading = messages.find((m) => m.fetching) ? true : false;

    const regenerateContent = () => {
        setMessages(prev => {
            const newMsgs = [...prev];
            newMsgs.pop();
            newMsgs.pop();
            return newMsgs;
        });
        handleSend(data?.snippet && messages.length === 1 ? data.message : messages[messages.length - 2].content);
    }

    const handleLoad = () => {
        controllerRef.current?.abort();
    }

    const handleScroll = () => {
        setPromptScroll(true);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setPromptScroll(false), 1000);
    }

    useEffect(() => {
        const ele = document.querySelector(".ai-prompt-list");
        ele?.addEventListener("scroll", handleScroll);
        return () => {
            controllerRef.current?.abort();
            ele?.removeEventListener("scroll", handleScroll);
        }
    }, []);

    const openChatHistory = () => {
        setOpenLeftPanel(openLeftPanel => !openLeftPanel);
    }

    const handleCancel = () => {
        setOpenRightDrawer({
            drawerType: null,
            drawerProps: {}
        });
    }

    return (
        <>
            <CreateTaskHeader
                buttonContent={data?.title ? data.title : "Atmosly AI Assistant"}
                onUpdate={openChatHistory}
                onCancel={handleCancel}
            />
            <AIChatHistory open={openLeftPanel} />
            <Flex vertical style={outerContainerStyle}>
                <XProvider>
                    {console.log("My messages: ", messages)}
                    <div style={{ height: "100%", overflowY: 'hidden', padding: 16 }}>
                        <Bubble.List
                            roles={roles}
                            autoScroll={true}
                            style={{ height: "inherit" }}
                            items={messages.map((msg, i) => ({
                                key: i,
                                header: msg.role === "user" ? <Flex>
                                    {msg.attachments?.map((item) => <Attachments.FileCard key={item.uid} item={item} style={{
                                        padding: "5px",
                                        width: "fit-content"
                                    }} />)}
                                </Flex> : undefined,
                                content: msg.content,
                                role: msg.role,
                                loading: msg.loading,
                                messageRender: (content) => {
                                    // if (msg.role === "assistant") {
                                        return <GenerateMessage
                                            content={content}
                                            i={i}
                                        />
                                    // }
                                    // else {
                                    //     return content;
                                    // }
                                },
                                footer: (msg.role === "assistant" && i === messages.length - 1 && i !== 0 && !msg.loading) ? <Space>
                                    <Button color="default" title="Re-generate response" variant="text" size="small" icon={<SyncOutlined />} onClick={regenerateContent} />
                                    <Button
                                        color="default"
                                        variant="text"
                                        size="small"
                                        title="Copy"
                                        onClick={() => copyText(msg.content)}
                                        icon={<CopyOutlined />}
                                    />
                                </Space> : undefined
                            }))}
                        />
                    </div>
                    {messages.length < 2 && !data?.snippet && <div onScroll={handleScroll}>
                        <Prompts
                            title="What can I help with ?"
                            items={promptItems}
                            onItemClick={(item) => handleSend(item.data.description)}
                            classNames={{
                                list: `ai-prompt-list ${promptScroll ? "scroll" : ""}`
                            }}
                            style={{
                                padding: "20px",
                            }}
                            styles={promptStyle}
                        />
                    </div>}
                    <div>
                        <Sender
                            ref={senderRef}
                            header={senderHeader}
                            value={message}
                            onChange={handleChange}
                            onSubmit={handleSend}
                            loading={senderLoading}
                            placeholder="Ask Me Anything"
                            autoSize={{ minRows: 3, maxRows: 3 }}
                            style={senderStyle}
                            footer={({ components }) => AIAgentFooter({ components, loading: senderLoading, open, setOpen, handleLoad, data: message, attachments: items })}
                            allowSpeech
                            actions={false}
                        />
                    </div>
                </XProvider>
            </Flex>
        </>
    );
}
